import React, { useMemo } from 'react';

import {
  AvatarGroup,
  Box,
  Card,
  ColorMode,
  Flex,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import Carousel from 'react-multi-carousel';

import Actions from '@/components/Defi/Actions';
import { NormalizedDefiItem } from '@/components/Defi/defi-page.types';
import { renderAvatarwithSVG } from '@/components/Defi/helpers';
import { xTokenMapping } from '@/constants/constants';
import { defiCurrencyIconMapping } from '@/constants/dappWalletMapping';
import { DefiData as ApyAndTvlDataType } from '@/customtypes/global';
import BaseImage from '@/dapp-sdk-v2/components/Image';
import { useDefiPageCMSData } from '@/providers/CMSDataProvider';
import { TextPrimary, TextSecondary } from '@/themes';
import { getCurrentXToken } from '@/utils/common';
import { formatNumber } from '@/utils/globalCommon';

import sdIcon from '@/assets/images/sd.png';

import 'react-multi-carousel/lib/styles.css';

const responsive = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 5000, min: 3000 },
    items: 5,
  },
  largeDesktop: {
    breakpoint: { max: 3000, min: 2000 },
    items: 4,
  },

  desktop: {
    breakpoint: { max: 2000, min: 950 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 950, min: 600 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 600, min: 0 },
    items: 1,
  },
};

const DefiTopOpportunity: React.FC<{
  items: NormalizedDefiItem[];
  apyAndTvlData: ApyAndTvlDataType;
  stakingAPY: number;
}> = ({ items, apyAndTvlData, stakingAPY }) => {
  const allDefiData = useDefiPageCMSData((data) => data.defi);

  const { colorMode } = useColorMode();

  const data = useMemo(() => {
    if (allDefiData?.defi_top_opportunities == null) {
      return null;
    }

    return allDefiData.defi_top_opportunities.map((topOpp) =>
      items.find((item) => item.apiKey === topOpp.item_key),
    );
  }, [allDefiData?.defi_top_opportunities, items]);

  if (data == null) {
    return null;
  }

  return (
    <Flex flexDir="column" mt={4} gap={4}>
      <TextSecondary fontWeight={600} ml={1}>
        Top Opportunities
      </TextSecondary>

      <Carousel responsive={responsive}>
        {(data.filter((x) => !!x) as NormalizedDefiItem[]).map((item) => (
          <Card
            key={item.apiKey}
            px={6}
            py={4}
            borderRadius={24}
            mr={4}
            boxShadow="none"
            gap={4}
          >
            <Flex flexDir="column" gap={6}>
              <Flex justifyContent="space-between">
                <DefiProvider item={item} />
                <DefiAvatar item={item} colorMode={colorMode} />
              </Flex>

              <Flex justifyContent="space-between" alignContent="stretch">
                <DefiTvlRenderer
                  item={item}
                  apyAndTvlData={apyAndTvlData}
                  stakingAPY={stakingAPY}
                />

                <Box
                  w="1px"
                  bg="#F5F5F7"
                  border={0}
                  display={{ base: 'none', md: 'block' }}
                />

                <DefiApyRenderer
                  item={item}
                  apyAndTvlData={apyAndTvlData}
                  stakingAPY={stakingAPY}
                />
              </Flex>

              <Flex>
                <Tag
                  size="md"
                  bg={'#F0FFF4'}
                  color={'#38A169'}
                  mt="1"
                  fontWeight={700}
                  p="4px 8px"
                  borderRadius="6px"
                >
                  <TagLabel>{item.sectionTitle}</TagLabel>
                </Tag>
              </Flex>
            </Flex>

            <Flex>
              <Actions
                isPaused={apyAndTvlData?.[item.apiKey]?.paused}
                id={item.apiKey}
                type={item.type}
                links={item.links}
                onlyPrimary
              />
            </Flex>
          </Card>
        ))}
      </Carousel>
    </Flex>
  );
};

const DefiAvatar: React.FC<{
  item: NormalizedDefiItem;
  colorMode: ColorMode;
}> = ({ item, colorMode }) => {
  const pathname = usePathname();
  const xToken = getCurrentXToken(pathname);

  const isSDPool = item.isSDPool;

  return (
    <Flex gap="4" align="center">
      <AvatarGroup spacing="-1" size="sm">
        {renderAvatarwithSVG(
          isSDPool
            ? sdIcon
            : defiCurrencyIconMapping[xToken.toLowerCase()][colorMode],
          isSDPool ? 'SD' : xTokenMapping[xToken],
        )}

        {item?.tokenIconKey &&
          renderAvatarwithSVG(
            defiCurrencyIconMapping[item?.tokenIconKey?.toLowerCase() || '']?.[
              colorMode
            ],
            'token',
          )}
      </AvatarGroup>
    </Flex>
  );
};

const DefiProvider: React.FC<{ item: NormalizedDefiItem }> = ({ item }) => {
  return (
    <Flex align="center" alignItems="center" gap="4px" flex="1">
      <AvatarGroup
        spacing="-0.5"
        size="sm"
        className="platformIconGroup"
        rounded="50%"
        overflow="hidden"
      >
        {item.pairPlatform && (
          <BaseImage
            height={28}
            width={28}
            src={item?.pairPlatform?.icon_key ?? ''}
            alt={item?.pairPlatform?.title || 'platform'}
          />
        )}
        <BaseImage
          height={28}
          width={28}
          src={item?.platform?.icon_key ?? ''}
          alt={item?.platform?.title || 'platform'}
        />
      </AvatarGroup>
      <TextPrimary fontSize={14} fontWeight={500}>
        {`${
          item.pairPlatform ? `${item.pairPlatform?.title} - ` : ''
        }${item?.platform?.title}`}
      </TextPrimary>
    </Flex>
  );
};

const DefiTvlRenderer: React.FC<{
  item: NormalizedDefiItem;
  apyAndTvlData: ApyAndTvlDataType;
  stakingAPY: number;
}> = ({ item, apyAndTvlData }) => {
  const TVL = apyAndTvlData[item.apiKey]?.tvl;
  const tvlTooltip = apyAndTvlData[item.apiKey]?.tvlTooltip;

  return (
    <Flex align="start" gap={1} flexDir="column">
      <Text fontSize="20px" fontWeight="700" color="primary">
        {TVL ? `$${TVL}` : '-'}
      </Text>

      <Text color="textSecondary" fontSize="14px">
        TVL
        {tvlTooltip && tvlTooltip.show && (
          <Tooltip label={tvlTooltip.text} placement="top">
            <AiOutlineInfoCircle />
          </Tooltip>
        )}
      </Text>
    </Flex>
  );
};

const DefiApyRenderer: React.FC<{
  item: NormalizedDefiItem;
  apyAndTvlData: ApyAndTvlDataType;
  stakingAPY: number;
}> = ({ item, apyAndTvlData, stakingAPY }) => {
  const apy = useMemo(() => {
    let _apy: string | number | undefined = apyAndTvlData[item.apiKey]?.apy;

    if (item?.should_add_staking_apy) {
      _apy = Number(_apy) + stakingAPY;
    }

    if (_apy) {
      _apy = formatNumber(Number(_apy), false, 2);
    }

    return _apy;
  }, [item, apyAndTvlData, stakingAPY]);

  const apyTooltip = apyAndTvlData[item.apiKey]?.apyTooltip;

  return (
    <Flex align="start" gap={1} flexDir="column">
      <Text fontSize="20px" fontWeight="700" color="primary">
        {apy ? `${apy}%` : '-'}
      </Text>

      <Text color="textSecondary" fontSize="14px">
        Estimated APY
        {apyTooltip && apyTooltip.show && (
          <Tooltip label={apyTooltip.text} placement="top">
            <AiOutlineInfoCircle />
          </Tooltip>
        )}
      </Text>
    </Flex>
  );
};

export { DefiTopOpportunity };
